import { EnvironmentConfig } from 'src/environments/environment.model';
import { environment } from 'src/environments/environment';
import { isLocalhostOrIp } from './domain-resolver.util';
import { isPreviewHost, isRegisterHost, isSharedHost } from './host-classification.util';

function trimTrailingSlash(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\/+$/, '');
}

function normalizeHostname(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().split(':')[0];
}

/**
 * Applies runtime environment overrides for preview hosts and branded custom shop
 * domains before Angular bootstraps.
 *
 * Preview frontends should call their colocated preview backend via same-origin.
 * The same applies to custom shop domains (a client domain CNAMEd to the App
 * Service): the frontend served from that host must call its own origin, because
 * the build-time apiUrl points at a fixed environment host and would route API
 * traffic to the wrong backend.
 *
 * Known shared hosts (main storefront, admin, compass, register) and localhost/IP
 * keep the configured apiUrl. Razorpay checkout remains on the verified DevTest
 * host and is handled by the preview handoff flow instead of being overridden here.
 */
export function applyRuntimeEnvironmentOverrides(
  runtimeEnvironment: EnvironmentConfig = environment,
  runtimeLocation: Pick<Location, 'hostname' | 'origin'> = window.location
): boolean {
  const hostname = normalizeHostname(runtimeLocation.hostname);
  const origin = trimTrailingSlash(runtimeLocation.origin);

  if (!hostname || !origin) {
    return false;
  }

  const keepsConfiguredApi = isSharedHost(hostname)
    || isRegisterHost(hostname)
    || isLocalhostOrIp(hostname);

  if (!isPreviewHost(hostname) && keepsConfiguredApi) {
    return false;
  }

  runtimeEnvironment.apiUrl = origin;
  runtimeEnvironment.defaultShopDomain = hostname;
  runtimeEnvironment.facebookOAuthRedirectUri = `${origin}/auth/callback`;

  return true;
}
